/**
 * Buddy Equipment Modal
 * Floating overlay for managing a fishing buddy's equipment
 * Reuses silhouette + grouped inventory pattern from PlayerEquipmentManager
 */

import { useState } from 'react';
import { useGameStore } from '@/state';
import { EquipmentSlot, EquipmentDefinition } from '@/types';
import { EQUIPMENT_DATA } from '@/data';
import { SLOT_CONFIG, getActiveSlots } from '@/data/equipment/slotConfig';
import { equipmentManager } from '@/systems';
import { EquipmentSlotButton } from '../equipment/EquipmentSlotButton';
import { SLOT_POSITIONS } from '../equipment/PlayerEquipmentManager';

interface BuddyEquipmentModalProps {
  buddySlotNumber: number;
  onClose: () => void;
}

export function BuddyEquipmentModal({ buddySlotNumber, onClose }: BuddyEquipmentModalProps) {
  const buddy = useGameStore((state) =>
    state.playerState.fishingBuddies.find((b) => b.slotNumber === buddySlotNumber)
  );
  const ownedEquipment = useGameStore((state) => state.playerState.ownedEquipment);
  const [selectedSlot, setSelectedSlot] = useState<EquipmentSlot | null>(null);

  if (!buddy) return null;

  const buddyName = buddy.name || `Buddy ${buddy.slotNumber}`;
  const availableQty = equipmentManager.getAvailableQuantities();
  const equipStats = equipmentManager.calculateBuddyEquipmentStats(buddySlotNumber);

  const handleSlotClick = (slot: EquipmentSlot) => {
    setSelectedSlot(selectedSlot === slot ? null : slot);
  };

  const handleEquipItem = (equipmentId: string) => {
    if (!selectedSlot) return;
    const equipment = EQUIPMENT_DATA[equipmentId];
    if (equipment.slot === selectedSlot) {
      equipmentManager.equipBuddyItem(buddySlotNumber, equipmentId);
      setSelectedSlot(null);
    }
  };

  const handleUnequip = (slot: EquipmentSlot) => {
    equipmentManager.unequipBuddyItem(buddySlotNumber, slot);
    setSelectedSlot(null);
  };

  const getEquippedItem = (slot: EquipmentSlot): EquipmentDefinition | null => {
    const itemId = buddy.equippedGear[slot];
    return itemId ? EQUIPMENT_DATA[itemId] : null;
  };

  // Group owned equipment by slot — show items where available > 0 OR equipped on THIS buddy
  const activeSlots = getActiveSlots(buddy.equippedGear);
  const groupedItems = activeSlots.map((slot) => {
    const meta = SLOT_CONFIG[slot];
    const equippedOnBuddy = buddy.equippedGear[slot];
    const items = Object.keys(ownedEquipment)
      .filter((id) => {
        if (EQUIPMENT_DATA[id]?.slot !== slot) return false;
        // Show if available OR already equipped on this buddy
        return (availableQty[id] || 0) > 0 || equippedOnBuddy === id;
      })
      .map((id) => ({
        id,
        definition: EQUIPMENT_DATA[id],
        data: ownedEquipment[id],
        isEquippedOnBuddy: equippedOnBuddy === id,
        availableCount: availableQty[id] || 0,
      }));
    return { slot, meta, items };
  }).filter((group) => group.items.length > 0);

  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={onClose}>
      <div
        className="bg-gray-800 rounded-xl border border-gray-600 shadow-2xl max-w-2xl w-full max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-700">
          <div>
            <h3 className="text-lg font-bold text-white">⚙️ {buddyName}'s Equipment</h3>
            <p className="text-xs text-gray-400">Click a slot to equip items from your shared inventory</p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl leading-none px-2"
          >
            ×
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Left: Character + Slots */}
            <div className="flex flex-col items-center space-y-4">
              {/* Equipment Stats */}
              <div className="w-full bg-gray-700/50 rounded-lg p-3 border border-gray-600">
                <h4 className="text-sm font-bold text-gray-300 mb-2">Equipment Stats</h4>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-gray-400">Speed:</span>
                    <span className="text-green-400 ml-1">+{Math.round((equipStats.fishingSpeed || 0) * 100)}%</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Catch:</span>
                    <span className="text-blue-400 ml-1">+{Math.round((equipStats.catchChance || 0) * 100)}%</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Quality:</span>
                    <span className="text-purple-400 ml-1">+{Math.round((equipStats.qualityBonus || 0) * 100)}%</span>
                  </div>
                  <div>
                    <span className="text-gray-400">Value:</span>
                    <span className="text-yellow-400 ml-1">+{Math.round((equipStats.valueBonus || 0) * 100)}%</span>
                  </div>
                </div>
              </div>

              {/* Character Silhouette + Slots */}
              <div className="relative w-56 h-72 bg-gray-700/30 rounded-lg border-2 border-gray-600 flex items-center justify-center">
                <div className="text-8xl opacity-20">🧑‍🤝‍🧑</div>

                {activeSlots.map((slot) => {
                  const meta = SLOT_CONFIG[slot];
                  const posClass = SLOT_POSITIONS[meta.silhouettePosition] || '';
                  return (
                    <div key={slot} className={`${posClass} [&:hover]:z-50`}>
                      <EquipmentSlotButton
                        slot={slot}
                        equipped={getEquippedItem(slot)}
                        isSelected={selectedSlot === slot}
                        onClick={() => handleSlotClick(slot)}
                        onUnequip={() => handleUnequip(slot)}
                      />
                    </div>
                  );
                })}
              </div>
            </div>

            {/* Right: Grouped Inventory */}
            <div className="space-y-3">
              <h4 className="text-sm font-bold text-gray-300">🎒 Available Equipment</h4>

              {groupedItems.length === 0 ? (
                <div className="text-center py-8 text-gray-400 text-sm">
                  No equipment available. Purchase more gear or unequip from the player!
                </div>
              ) : (
                <div className="max-h-80 overflow-y-auto space-y-4">
                  {groupedItems.map(({ slot, meta, items }) => (
                    <div key={slot}>
                      {/* Section Header */}
                      <div className="sticky top-0 bg-gray-800/90 backdrop-blur-sm px-2 py-1.5 rounded border-b border-gray-600 mb-2 z-10">
                        <span className="text-sm font-semibold text-gray-300">
                          {meta.icon} {meta.pluralLabel}
                          <span className="text-gray-500 ml-1 text-xs">({items.length})</span>
                        </span>
                      </div>

                      {/* Items in group */}
                      <div className="space-y-2">
                        {items.map((item) => {
                          const isCompatible = selectedSlot && item.definition.slot === selectedSlot;
                          const canEquip = isCompatible && (item.availableCount > 0 || item.isEquippedOnBuddy);

                          return (
                            <button
                              key={item.id}
                              onClick={() => handleEquipItem(item.id)}
                              disabled={!canEquip && selectedSlot !== null}
                              className={`w-full text-left p-3 rounded-lg border-2 transition-all ${
                                canEquip
                                  ? 'bg-blue-600/30 border-blue-500 hover:bg-blue-600/50 animate-pulse'
                                  : selectedSlot
                                  ? 'bg-gray-800/30 border-gray-700 opacity-30 cursor-not-allowed'
                                  : 'bg-gray-700/50 border-gray-600 hover:border-gray-500 hover:bg-gray-700'
                              } ${item.isEquippedOnBuddy ? 'ring-2 ring-green-500' : ''}`}
                            >
                              <div className="flex items-center justify-between">
                                <div className="flex-1">
                                  <div className="flex items-center gap-2">
                                    <span className="font-bold text-white text-sm">{item.definition.name}</span>
                                    {item.isEquippedOnBuddy && <span className="text-xs text-green-400">✓ Equipped</span>}
                                  </div>
                                  <div className="text-xs text-gray-400 mt-1">
                                    {item.definition.description || 'No description'}
                                  </div>
                                  <div className="flex items-center gap-2 mt-1">
                                    <span className="text-xs text-gray-500">
                                      Avail: {item.isEquippedOnBuddy ? item.availableCount + 1 : item.availableCount}/{item.data.quantity}
                                    </span>
                                  </div>
                                </div>

                                {/* Stats */}
                                <div className="text-xs text-right space-y-1">
                                  {item.definition.stats.fishingSpeed && (
                                    <div className="text-green-400">Speed +{Math.round(item.definition.stats.fishingSpeed * 100)}%</div>
                                  )}
                                  {item.definition.stats.catchChance && (
                                    <div className="text-blue-400">Catch +{Math.round(item.definition.stats.catchChance * 100)}%</div>
                                  )}
                                  {item.definition.stats.qualityBonus && (
                                    <div className="text-purple-400">Quality +{Math.round(item.definition.stats.qualityBonus * 100)}%</div>
                                  )}
                                </div>
                              </div>
                            </button>
                          );
                        })}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
